import { inspectOwnedResource, notRequiredResult, unmanagedResourceError, unmanagedResourceMessage } from "./results";
import { isResourceApplicable } from "./applicability";
import { findMappingById, findMappingByName, getAdapterQuestions } from "@/features/adapters/model/helpers";
import {
  getAdapterMappingColumns,
  getUnresolvedAdapterMappingGuidance,
  getAdapterGuidanceLabel,
  getAdapterGuidanceTodo,
  hasAdapterGuidanceError,
  isMissingAdapterMappingColumn,
  resolveAdapterMappingGuidance,
} from "@/features/adapters/model/mappingGuidance";
import { applyAdapterMappingRequirements } from "@/features/adapters/model/mappingMerge";
import { applyAdapterBindings, getAdapterBoundValues } from "@/features/adapters/model/bindings";
import type { ReleaseMapping } from "@packages/commons";
import { getMappingRecordFields } from "@/features/adapters/model/mappingRecordFields";
import { releaseCollection } from "./collections";
import type {
  AdapterApplyAnswers,
  AdapterMappingDefinition,
  ReleaseWithAdapterCollections,
} from "@/features/adapters/model/types";
import type {
  MappingResourceDefinition,
  ResourceApplyResult,
  ResourceDriver,
} from "./types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFullMappingPayload = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) && ("columns" in value || "entityMapping" in value || "mode" in value || "data" in value);

export const buildMappingResourcePayload = (
  mapping: AdapterMappingDefinition,
  answers?: AdapterApplyAnswers,
): Record<string, unknown> => {
  const basePayload = { name: mapping.name, type: mapping.type, mode: mapping.mode };
  const columns = getAdapterMappingColumns(mapping);
  const templateData = isRecord(mapping.data) && columns.length ? { ...mapping.data, columns } : mapping.data;
  const requiredGuidance = resolveAdapterMappingGuidance(mapping, templateData).filter((item) => !item.optional);
  const payload = isFullMappingPayload(templateData)
    ? applyAdapterMappingRequirements({ ...templateData, ...basePayload }, requiredGuidance)
    : applyAdapterMappingRequirements({ ...basePayload, data: templateData }, requiredGuidance);
  return answers ? applyAdapterBindings(payload, mapping, answers) : payload;
};

const findCreatedMapping = (
  release: ReleaseWithAdapterCollections,
  definition: AdapterMappingDefinition,
): string => {
  const created = findMappingByName(release, definition.name);
  if (!created?.id) throw new Error(`Could not find created mapping "${definition.name}".`);
  return created.id;
};

type MappingKind = "mapping" | "deltaMapping";

const createMappingResourceDriver = (
  kind: MappingKind,
): ResourceDriver<MappingResourceDefinition, MappingKind> => ({
  kind,
  setup: "automatic",
  collection: kind === "mapping"
    ? releaseCollection<ReleaseMapping>("mapping", "mapping", (mapping) => Boolean(mapping.id && mapping.name && mapping.type))
    : undefined,
  describe: (declaration) => declaration.resource.name,
  openAfterCreate: true,
  ownedKind: "mapping",
  persistsOwnership: true,
  define: (context, owner, declaration) => {
    const resource = declaration.resource;
    const applicable = isResourceApplicable(context.answers, owner, resource);
    return [{
          key: owner.stepId,
          stepId: owner.stepId,
          definitionKey: resource.key,
          kind,
          label: resource.name,
          applicable,
          resourceKey: resource.key,
          resource,
        }];
  },
  inspect: (context, definition, resourceId) => {
    const saved = findMappingById(context.release, resourceId);
    const conflict = saved ? undefined : findMappingByName(context.release, definition.resource.name);
    const values = (saved?.id ? context.draftsByMappingId?.[saved.id] : undefined) ?? saved;
    const href = saved?.id ? `/build/mapping?mappingId=${encodeURIComponent(saved.id)}` : undefined;
    const guidance = values
      ? resolveAdapterMappingGuidance(definition.resource, values)
      : (definition.resource.guidance ?? []);
    const setupGuidance = guidance.filter((item) => item.requirements.length > 0);
    const requirementContext = {
      recordFields: getMappingRecordFields(context.release, values),
      boundValues: getAdapterBoundValues(definition.resource, context.answers, getAdapterQuestions(context.definition)),
    };
    const unresolved = values
      ? getUnresolvedAdapterMappingGuidance(values, setupGuidance, requirementContext)
      : setupGuidance;
    const unresolvedIds = new Set(unresolved.map((guidance) => guidance.id));
    const failing = values ? unresolved.find((guidance) => hasAdapterGuidanceError(values, guidance, requirementContext)) : undefined;
    const inputs = values
      ? setupGuidance.map((guidance) => {
          const complete = !unresolvedIds.has(guidance.id);
          return {
            id: guidance.id,
            title: getAdapterGuidanceLabel(guidance),
            instructions: guidance.instructions,
            complete,
            href: isMissingAdapterMappingColumn(guidance) ? undefined : href,
            todo: complete ? [] : getAdapterGuidanceTodo(values, guidance, requirementContext),
            missingColumn: isMissingAdapterMappingColumn(guidance),
            ...(!complete && hasAdapterGuidanceError(values, guidance, requirementContext) ? { error: true } : {}),
          };
        })
      : [];
    const resourceNoun = definition.kind === "deltaMapping" ? "delta mapping" : "mapping";
    const resourceLabel = definition.kind === "deltaMapping" ? "Delta mapping" : "Mapping";
    return inspectOwnedResource(definition, {
      found: saved,
      conflict,
      inputs,
      messages: {
        conflict: unmanagedResourceMessage(`A ${resourceNoun} with this name`),
        missing: `${resourceLabel} will be created when the integration setup runs.`,
        // What breaks imports, then what is left to do, then the field's own instructions.
        error: failing
          ? [getAdapterGuidanceTodo(values, failing, requirementContext)[0], failing.instructions].filter(Boolean).join(" ")
          : undefined,
        incomplete: unresolved[0]
          ? [values ? getAdapterGuidanceTodo(values, unresolved[0], requirementContext)[0] : undefined, unresolved[0].instructions]
              .filter(Boolean)
              .join(" ")
          : `${resourceLabel} needs project-specific configuration.`,
        ready: `Managed ${resourceNoun} is ready.`,
      },
      href,
      linkLabel: href ? `Open ${resourceNoun}` : undefined,
      action:
        definition.applicable && !saved && !conflict
          ? {
              kind: "apply",
              label: definition.kind === "deltaMapping" ? "Create delta mapping" : "Create mapping",
              stepId: definition.stepId,
            }
          : undefined,
    });
  },
  ensure: async (context, definition, resourceId): Promise<ResourceApplyResult> => {
    if (!definition.applicable) return notRequiredResult(definition);
    const existing = findMappingById(context.release, resourceId);
    const conflict = existing ? undefined : findMappingByName(context.release, definition.resource.name);
    const resourceNoun = definition.kind === "deltaMapping" ? "delta mapping" : "mapping";
    if (conflict) {
      throw unmanagedResourceError(`${resourceNoun[0]?.toUpperCase()}${resourceNoun.slice(1)} "${definition.resource.name}"`);
    }

    // Adapters only create mappings; once one exists, it is edited in the mapping editor.
    if (existing) return { message: `The managed ${resourceNoun} already exists.` };

    const payload = buildMappingResourcePayload(definition.resource, context.answers);
    const release = await context.operations.createMapping(payload);
    const mappingId = findCreatedMapping(release, definition.resource);
    return { release, resourceId: mappingId, message: `Created ${resourceNoun}.` };
  },
});

export const mappingResourceDriver = { ...createMappingResourceDriver("mapping"), kind: "mapping" as const };
export const deltaMappingResourceDriver = { ...createMappingResourceDriver("deltaMapping"), kind: "deltaMapping" as const };
